import { useEffect, useMemo, useState } from "react";
import { checkDeviceRequirements } from "../firebase/checkDeviceRequirements";

const compatibilityCache = new Map();
const inFlightChecks = new Map();

function getCacheKey(requireCameraAccess) {
  return requireCameraAccess ? "active-camera-check" : "passive-route-check";
}

function buildResolvedState(isCompatible) {
  return {
    status: isCompatible ? "compatible" : "incompatible",
    isCompatible,
  };
}

function getCachedState(cacheKey) {
  return (
    compatibilityCache.get(cacheKey) || {
      status: "checking",
      isCompatible: null,
    }
  );
}

export function __resetDeviceCompatibilityCache() {
  compatibilityCache.clear();
  inFlightChecks.clear();
}

/**
 * React hook that determines whether the current device/browser meets the
 * app’s baseline requirements.
 *
 * @param {{ requireCameraAccess?: boolean }} [options]
 * @returns {{ status: "checking"|"compatible"|"incompatible", isCompatible: boolean|null }}
 */
export default function useDeviceCompatibility(options = {}) {
  const requireCameraAccess = Boolean(options.requireCameraAccess);
  const cacheKey = useMemo(
    () => getCacheKey(requireCameraAccess),
    [requireCameraAccess],
  );
  const [state, setState] = useState(() => getCachedState(cacheKey));

  useEffect(() => {
    let isMounted = true;

    const cached = compatibilityCache.get(cacheKey);
    if (cached) {
      setState(cached);
      return () => {
        isMounted = false;
      };
    }

    const runCheck = async () => {
      let checkPromise = inFlightChecks.get(cacheKey);

      if (!checkPromise) {
        const userAgent =
          typeof navigator !== "undefined" ? navigator.userAgent : "";

        checkPromise = checkDeviceRequirements(userAgent, {
          requireCameraAccess,
        })
          .then((result) => buildResolvedState(Boolean(result)))
          .catch((error) => {
            console.error("Device compatibility check failed:", error);
            return buildResolvedState(false);
          })
          .then((nextState) => {
            compatibilityCache.set(cacheKey, nextState);
            inFlightChecks.delete(cacheKey);
            return nextState;
          });

        inFlightChecks.set(cacheKey, checkPromise);
      }

      const nextState = await checkPromise;
      if (isMounted) {
        setState(nextState);
      }
    };

    runCheck();

    return () => {
      isMounted = false;
    };
  }, [cacheKey, requireCameraAccess]);

  return state;
}
